/** Botón móvil con variantes, tamaños y estados. */
import { clsx } from 'clsx';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
type ButtonSize = 'sm' | 'md' | 'lg';

type MobileButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  fullWidth?: boolean;
  icon?: ReactNode;
  iconRight?: ReactNode;
  children: ReactNode;
};

const variantStyles: Record<ButtonVariant, string> = {
  primary: `
    bg-primary text-white
    hover:bg-primary-hover active:bg-primary-dark
    disabled:bg-gray-300 disabled:text-gray-500
  `,
  secondary: `
    bg-secondary-light text-secondary-dark-text
    hover:brightness-95 active:brightness-90
    disabled:bg-gray-100 disabled:text-gray-400
  `,
  ghost: `
    bg-transparent text-primary
    hover:bg-primary-light active:brightness-95
    disabled:text-gray-300
  `,
  danger: `
    bg-error text-white
    hover:bg-error-dark active:bg-error-dark
    disabled:bg-gray-300 disabled:text-gray-500
  `,
  success: `
    bg-success text-white
    hover:bg-success-dark active:bg-success-dark
    disabled:bg-gray-300 disabled:text-gray-500
  `,
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: 'h-11 px-4 text-sm gap-1.5',
  md: 'h-12 px-5 text-base gap-2',
  lg: 'h-14 px-6 text-lg gap-2.5',
};

export function MobileButton({
  variant = 'primary',
  size = 'md',
  loading = false,
  fullWidth = false,
  icon,
  iconRight,
  children,
  disabled,
  className,
  ...props
}: MobileButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled || loading}
      className={clsx(
        // Base v2: pill en todas las variantes
        'inline-flex items-center justify-center font-semibold',
        'rounded-full transition-all duration-150',
        'active:scale-[0.97] active:shadow-sm',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
        'touch-manipulation select-none',
        
        // Variant
        variantStyles[variant],
        
        // Size
        sizeStyles[size],
        
        // Full width
        fullWidth && 'w-full',
        
        // Disabled
        (disabled || loading) && 'cursor-not-allowed opacity-60',
        
        // Custom
        className,
      )}
      {...props}
    >
      {loading ? (
        <svg
          className="h-5 w-5 animate-spin"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : icon ? (
        <span className="flex-shrink-0">{icon}</span>
      ) : null}
      
      <span>{children}</span>
      
      {iconRight && !loading && (
        <span className="flex-shrink-0">{iconRight}</span>
      )}
    </button>
  );
}
